import React, { useState, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { BuildingIcon, UserIcon, SearchIcon, Loader2Icon, CheckCircle2Icon, LandmarkIcon, SmartphoneIcon } from 'lucide-react';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { THAI_PROVINCES, THAI_BANKS } from '../mocks/mockSuppliers';
export function SupplierSelfService() {
  const {
    t
  } = useTranslation('suppliers');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [supplierType, setSupplierType] = useState<'company' | 'individual'>('company');
  // DBD Lookup State
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [dbdMeta, setDbdMeta] = useState<{
    registerDate?: string;
    juristicStatus?: string;
    juristicType?: string;
    isMockFallback?: boolean;
  }>({});
  const [formData, setFormData] = useState({
    // Shared
    email: '',
    phone: '',
    useAsBillingEmail: true,
    addressLine1: '',
    addressLine2: '',
    city: '',
    province: '',
    country: 'Maldives',
    postcode: '',
    // Company
    taxId: '',
    companyNameTh: '',
    companyNameEn: '',
    branch: '00000',
    vatRegistered: false,
    contactPerson: '',
    // Individual
    idType: 'citizen_id',
    idNumber: '',
    fullNameTh: '',
    fullNameEn: '',
    individualTaxId: '',
    // Payout method
    payoutMethod: 'bank_account',
    bankName: '',
    accountNumber: '',
    accountName: '',
    promptPayType: 'phone',
    promptPayId: '',
    promptPayName: ''
  });
  const handleLookup = async () => {
    if (!formData.taxId || formData.taxId.length !== 13) {
      setLookupError('Please enter a valid 13-digit Tax ID / Registration Number');
      setLookupSuccess(false);
      return;
    }
    setLookupError('');
    setIsLookingUp(true);
    setLookupSuccess(false);
    setDbdMeta({});
    const DBD_API_BASE = 'https://opendata.dbd.go.th/api/action/datastore_search';
    const resourceId = 'f008dbbf-ddfa-4e3a-bac4-358a1a2b9853';
    const apiUrl = `${DBD_API_BASE}?resource_id=${resourceId}&filters=${encodeURIComponent(JSON.stringify({
      juristicID: formData.taxId
    }))}&limit=1`;
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) throw new Error(`API returned status ${response.status}`);
      const data = await response.json();
      if (!data.success || !data.result?.records?.length) {
        setIsLookingUp(false);
        setLookupError(`No company found for Tax ID ${formData.taxId}. Please verify the number and try again.`);
        return;
      }
      const record = data.result.records[0];
      const companyNameLocal = record.juristicNameTH || record.juristicNameTh || record.companyNameTH || '';
      const companyNameEn = record.juristicNameEN || record.juristicNameEn || record.companyNameEN || '';
      const addressFull = record.addressDesc || record.address || record.juristicAddress || '';
      const registerDate = record.registerDate || record.registrationDate || '';
      const juristicStatus = record.juristicStatus || record.status || '';
      const juristicType = record.juristicType || record.entityType || '';
      let addressLine1 = addressFull;
      let addressLine2 = '';
      let city = '';
      let postcode = '';
      const postcodeMatch = addressFull.match(/\b(\d{5})\b\s*$/);
      if (postcodeMatch) {
        postcode = postcodeMatch[1];
        addressLine1 = addressFull.replace(postcodeMatch[0], '').trim();
      }
      const parts = addressLine1.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
      if (parts.length >= 3) {
        addressLine1 = parts.slice(0, Math.ceil(parts.length / 2)).join(', ');
        const remaining = parts.slice(Math.ceil(parts.length / 2));
        city = remaining.pop() || '';
        addressLine2 = remaining.join(', ');
      } else if (parts.length === 2) {
        addressLine1 = parts[0];
        city = parts[1];
      }
      setFormData(prev => ({
        ...prev,
        companyNameTh: companyNameLocal || prev.companyNameTh,
        companyNameEn: companyNameEn || prev.companyNameEn,
        addressLine1: addressLine1 || prev.addressLine1,
        addressLine2: addressLine2 || prev.addressLine2,
        city: city || prev.city,
        postcode: postcode || prev.postcode
      }));
      setDbdMeta({
        registerDate,
        juristicStatus,
        juristicType,
        isMockFallback: false
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    } catch (err: any) {
      console.warn('DBD API call failed, using mock fallback:', err.message);
      setFormData(prev => ({
        ...prev,
        companyNameTh: 'Test Company Limited',
        companyNameEn: 'Test Company Limited',
        addressLine1: 'H. Maaveyo, Boduthakurufaanu Magu',
        addressLine2: 'Henveiru',
        city: 'Malé',
        postcode: '20094'
      }));
      setDbdMeta({
        registerDate: '2020-03-15',
        juristicStatus: 'Active',
        juristicType: 'Limited Company',
        isMockFallback: true
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
  };
  if (isSubmitted) {
    return <div className="min-h-screen bg-[#1a2042] flex flex-col items-center justify-center p-4" data-id="element-13339">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center" data-id="element-13340">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-13341">
            <CheckCircle2Icon size={32} className="text-green-600" data-id="element-13342" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-13343">
            {t('pages.SupplierSelfService.details_submitted_successfully')}
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-13344">
            {t('pages.SupplierSelfService.thank_you_your_information_has_been')}
          </p>
          <p className="text-sm text-gray-500" data-id="element-13345">
            {t('pages.SupplierSelfService.you_can_close_this_page')}
          </p>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#1a2042] py-12 px-4 sm:px-6 lg:px-8" data-id="element-13346">
      <div className="max-w-3xl mx-auto" data-id="element-13347">
        {/* Header */}
        <div className="text-center mb-8" data-id="element-13348">
          <div className="flex items-center justify-center mb-6" data-id="element-13349">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/63313e770ee1f10008cdcca5_Pomelo-Primary-logo-white.png" alt={t('pages.SupplierSelfService.attrs.pomelo')} className="h-10" data-id="element-13350" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3" data-id="element-13351">
            {t('pages.SupplierSelfService.complete_your_supplier_profile')}
          </h1>
          <p className="text-gray-400" data-id="element-13352">
            {t('pages.SupplierSelfService.siam_fresh_market_co_ltd_has')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-id="element-13353">
          <div className="px-8 py-6 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-13354">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-13355">
              {t('pages.SupplierForm.supplier_information')}
            </h2>

            {/* Supplier Type Segmented Control */}
            <div className="flex p-1 bg-gray-100 rounded-lg" data-id="element-13356">
              <button type="button" onClick={() => setSupplierType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-13357">
                <BuildingIcon size={16} className="mr-2" data-id="element-13358" />
                {t('pages.SupplierDetail.company')}
              </button>
              <button type="button" onClick={() => setSupplierType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-13359">
                <UserIcon size={16} className="mr-2" data-id="element-13360" />
                {t('pages.SupplierDetail.individual')}
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-8" data-id="element-13361">
            {/* --- COMPANY FIELDS --- */}
            {supplierType === 'company' && <div className="space-y-6" data-id="element-13362">
                {/* DBD Lookup Section */}
                <div className="bg-gray-50 p-5 rounded-xl border border-gray-200" data-id="element-13363">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13364">
                    {t('pages.SupplierForm.tax_id_registration_number')}
                  </label>
                  <div className="flex gap-3" data-id="element-13365">
                    <input type="text" name="taxId" required maxLength={13} value={formData.taxId} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.13digit_registration_number')} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-13366" />

                    <button type="button" onClick={handleLookup} disabled={isLookingUp} className="flex items-center px-5 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] disabled:bg-[#E2231A]/70 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13367">
                      {isLookingUp ? <Loader2Icon size={18} className="animate-spin" data-id="element-13368" /> : <>
                          <SearchIcon size={18} className="mr-2" data-id="element-13369" />
                          {t('pages.SupplierForm.lookup')}
                        </>}
                    </button>
                  </div>

                  {lookupError && <p className="mt-2 text-sm text-red-600" data-id="element-13370">{lookupError}</p>}

                  {lookupSuccess && <div className="mt-4 space-y-2" data-id="element-13371">
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start" data-id="element-13372">
                        <CheckCircle2Icon size={20} className="text-yellow-700 mt-0.5 mr-3 flex-shrink-0" data-id="element-13373" />

                        <div data-id="element-13374">
                          <p className="text-sm font-medium text-yellow-800" data-id="element-13375">
                            {t('pages.SupplierSelfService.company_details_fetched_from_department_of')}
                          </p>
                          <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-yellow-700" data-id="element-13376">
                            {dbdMeta.registerDate && <>
                                <span data-id="element-13377">
                                  {t('pages.SupplierForm.registered')}{' '}
                                  {dbdMeta.registerDate}
                                </span>
                                <span className="w-1 h-1 rounded-full bg-yellow-400" data-id="element-13378"></span>
                              </>}
                            {dbdMeta.juristicStatus && <span className="font-semibold" data-id="element-13379">
                                {dbdMeta.juristicStatus}
                              </span>}
                            {dbdMeta.juristicType && <>
                                <span className="w-1 h-1 rounded-full bg-yellow-400" data-id="element-13380"></span>
                                <span data-id="element-13381">{dbdMeta.juristicType}</span>
                              </>}
                          </div>
                        </div>
                      </div>
                    </div>}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13382">
                  <div data-id="element-13383">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13384">
                      {t('pages.SupplierForm.company_name_local')}
                    </label>
                    <input type="text" name="companyNameTh" required value={formData.companyNameTh} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-13385" />
                  </div>
                  <div data-id="element-13386">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13387">
                      {t('pages.SupplierDetail.company_name_english')}
                    </label>
                    <input type="text" name="companyNameEn" value={formData.companyNameEn} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-13388" />
                  </div>
                  <div data-id="element-13389">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13390">
                      {t('pages.SupplierForm.branch_number')}
                    </label>
                    <input type="text" name="branch" required value={formData.branch} onChange={handleChange} placeholder="00000" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13391" />
                  </div>
                  <div data-id="element-13392">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13393">
                      {t('pages.SupplierForm.contact_person_name')}
                    </label>
                    <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13394" />
                  </div>
                </div>

                <label className="flex items-center cursor-pointer group w-fit" data-id="element-13395">
                  <div className="relative flex items-center justify-center" data-id="element-13396">
                    <input type="checkbox" name="vatRegistered" checked={formData.vatRegistered} onChange={handleChange} className="peer sr-only" data-id="element-13397" />

                    <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#E2231A] peer-checked:border-[#E2231A] transition-colors" data-id="element-13398"></div>
                    <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-13399">
                      <polyline points="20 6 9 17 4 12" data-id="element-13400"></polyline>
                    </svg>
                  </div>
                  <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-13401">
                    {t('pages.SupplierForm.vat_registered_company')}
                  </span>
                </label>
              </div>}

            {/* --- INDIVIDUAL FIELDS --- */}
            {supplierType === 'individual' && <div className="space-y-6" data-id="element-13402">
                <div className="flex gap-6 mb-2" data-id="element-13403">
                  <label className="flex items-center cursor-pointer" data-id="element-13404">
                    <input type="radio" name="idType" value="citizen_id" checked={formData.idType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13405" />

                    <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-13406">
                      {t('pages.SupplierForm.thai_citizen_id')}
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer" data-id="element-13407">
                    <input type="radio" name="idType" value="passport" checked={formData.idType === 'passport'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13408" />

                    <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-13409">
                      {t('pages.SupplierForm.passport')}
                    </span>
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13410">
                  <div className="md:col-span-2" data-id="element-13411">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13412">
                      {formData.idType === 'citizen_id' ? 'Citizen ID Number *' : 'Passport Number *'}
                    </label>
                    <input type="text" name="idNumber" required maxLength={formData.idType === 'citizen_id' ? 13 : undefined} value={formData.idNumber} onChange={handleChange} placeholder={formData.idType === 'citizen_id' ? '13-digit ID number' : 'Passport number'} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13413" />
                  </div>
                  <div data-id="element-13414">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13415">
                      {t('pages.SupplierForm.full_name_local')}
                    </label>
                    <input type="text" name="fullNameTh" value={formData.fullNameTh} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13416" />
                  </div>
                  <div data-id="element-13417">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13418">
                      {t('pages.SupplierForm.full_name')}
                    </label>
                    <input type="text" name="fullNameEn" required value={formData.fullNameEn} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13419" />
                  </div>
                  <div className="md:col-span-2" data-id="element-13420">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13421">
                      {t('pages.SupplierForm.tax_id_if_different_from_citizen')}
                    </label>
                    <input type="text" name="individualTaxId" value={formData.individualTaxId} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13422" />
                  </div>
                </div>
              </div>}

            {/* --- SHARED CONTACT FIELDS --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-gray-100" data-id="element-13423">
              <div data-id="element-13424">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13425">
                  {t('pages.SupplierForm.email_address')}
                </label>
                <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.enter_email_address')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13426" />
              </div>
              <div data-id="element-13427">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13428">
                  {t('pages.SupplierForm.phone_number')}
                </label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_0812345678')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-13429" />
              </div>
            </div>

            {/* Divider: Billing information */}
            <div className="relative py-2" data-id="element-13430">
              <div className="absolute inset-0 flex items-center" data-id="element-13431">
                <div className="w-full border-t border-gray-200" data-id="element-13432"></div>
              </div>
              <div className="relative flex justify-center" data-id="element-13433">
                <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13434">
                  {t('pages.SupplierForm.billing_information')}
                </span>
              </div>
            </div>

            {/* Billing Info Fields */}
            <div className="space-y-5" data-id="element-13435">
              <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_1')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13436" />

              <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_2')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13437" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13438">
                <div data-id="element-13439">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-13440">
                    {t('pages.SupplierForm.city_district')}
                  </label>
                  <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_khlong_toei')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13441" />
                </div>
                <div data-id="element-13442">
                  <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-13443">
                    {t('pages.BulkSupplierUpload.province')}
                  </label>
                  <SearchableSelect name="province" options={THAI_PROVINCES} value={formData.province} onChange={val => setFormData(prev => ({
                  ...prev,
                  province: val
                }))} placeholder={t('pages.SupplierForm.attrs.select_province')} data-id="element-13444" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13445">
                <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-13446">
                  {t('pages.SupplierForm.thailand')}
                </div>
                <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.postcode_5_digits')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-13447" />
              </div>
            </div>

            {/* Divider: Payout method */}
            <div className="relative py-2" data-id="element-13448">
              <div className="absolute inset-0 flex items-center" data-id="element-13449">
                <div className="w-full border-t border-gray-200" data-id="element-13450"></div>
              </div>
              <div className="relative flex justify-center" data-id="element-13451">
                <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-13452">
                  {t('pages.SupplierDetail.payout_method')}
                </span>
              </div>
            </div>

            <p className="text-xs text-gray-500 -mt-4" data-id="element-13453">
              {t('pages.SupplierSelfService.choose_how_you_would_like_to')}
            </p>

            {/* Payout Method Selection */}
            <div className="space-y-4" data-id="element-13454">
              {/* Bank Account Option */}
              <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'bank_account' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-13455">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-13456">
                  <input type="radio" name="payoutMethod" value="bank_account" checked={formData.payoutMethod === 'bank_account'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-13457" />

                  <div className="ml-3 flex items-center" data-id="element-13458">
                    <div className="w-8 h-8 rounded-full bg-yellow-50 flex items-center justify-center mr-3" data-id="element-13459">
                      <LandmarkIcon size={16} className="text-yellow-700" aria-hidden="true" data-id="element-13460" />
                    </div>
                    <div data-id="element-13461">
                      <div className="text-sm font-medium text-gray-900" data-id="element-13462">
                        {t('pages.SupplierDetail.bank_account')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-13463">
                        {t('pages.SupplierForm.direct_transfer_to_a_thai_bank')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payoutMethod === 'bank_account' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-13464">
                    <div data-id="element-13465">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13466">
                        {t('pages.SupplierDetail.bank_name')}
                      </label>
                      <SearchableSelect name="bankName" options={THAI_BANKS} value={formData.bankName} onChange={val => setFormData(prev => ({
                    ...prev,
                    bankName: val
                  }))} placeholder={t('pages.SupplierForm.attrs.select_a_bank')} data-id="element-13467" />
                    </div>
                    <div data-id="element-13468">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13469">
                        {t('pages.SupplierDetail.account_number')}
                      </label>
                      <input type="text" name="accountNumber" maxLength={12} placeholder={t('pages.SupplierForm.attrs.1012_digits')} value={formData.accountNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13470" />
                    </div>
                    <div data-id="element-13471">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13472">
                        {t('pages.SupplierDetail.account_name')}
                      </label>
                      <input type="text" name="accountName" placeholder={t('pages.SupplierForm.attrs.name_on_the_bank_account')} value={formData.accountName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13473" />
                    </div>
                  </div>}
              </div>

              {/* PromptPay Option */}
              <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'promptpay' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-13474">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-13475">
                  <input type="radio" name="payoutMethod" value="promptpay" checked={formData.payoutMethod === 'promptpay'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-13476" />

                  <div className="ml-3 flex items-center" data-id="element-13477">
                    <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center mr-3" data-id="element-13478">
                      <SmartphoneIcon size={16} className="text-purple-600" data-id="element-13479" />
                    </div>
                    <div data-id="element-13480">
                      <div className="text-sm font-medium text-gray-900" data-id="element-13481">
                        {t('pages.SupplierDetail.promptpay')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-13482">
                        {t('pages.SupplierForm.fast_transfer_via_phone_citizen_id')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payoutMethod === 'promptpay' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-13483">
                    <div data-id="element-13484">
                      <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-13485">
                        {t('pages.SupplierForm.promptpay_type')}
                      </label>
                      <div className="flex flex-wrap gap-4" data-id="element-13486">
                        <label className="flex items-center" data-id="element-13487">
                          <input type="radio" name="promptPayType" value="phone" checked={formData.promptPayType === 'phone'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13488" />

                          <span className="ml-2 text-sm text-gray-700" data-id="element-13489">
                            {t('pages.SupplierForm.phone_number_2')}
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-13490">
                          <input type="radio" name="promptPayType" value="citizen_id" checked={formData.promptPayType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13491" />

                          <span className="ml-2 text-sm text-gray-700" data-id="element-13492">
                            {t('pages.SupplierForm.citizen_id')}
                          </span>
                        </label>
                        <label className="flex items-center" data-id="element-13493">
                          <input type="radio" name="promptPayType" value="tax_id" checked={formData.promptPayType === 'tax_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-13494" />

                          <span className="ml-2 text-sm text-gray-700" data-id="element-13495">
                            {t('pages.BulkSupplierUpload.tax_id')}
                          </span>
                        </label>
                      </div>
                    </div>
                    <div data-id="element-13496">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13497">
                        {t('pages.SupplierDetail.promptpay_id')}
                      </label>
                      <input type="text" name="promptPayId" placeholder={formData.promptPayType === 'phone' ? '0XX-XXX-XXXX' : 'X-XXXX-XXXXX-XX-X'} value={formData.promptPayId} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13498" />
                    </div>
                    <div data-id="element-13499">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-13500">
                        {t('pages.SupplierForm.display_name_for_verification')}
                      </label>
                      <input type="text" name="promptPayName" value={formData.promptPayName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-13501" />
                    </div>
                  </div>}
              </div>
            </div>

            {/* Form Actions */}
            <div className="pt-6 border-t border-gray-100" data-id="element-13502">
              <button type="submit" className="w-full px-6 py-3 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13503">
                {t('pages.SupplierSelfService.submit_details')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>;
}